import React, { useEffect, useState } from "react";
import axios from "../api/axiosInstance";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import {
  FaSearch,
  FaPlus,
  FaTrash,
  FaPlayCircle,
  FaNewspaper,
} from "react-icons/fa";

const BlogTable = () => {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(
    () => parseInt(localStorage.getItem("pageSize")) || 10,
  );
  const [totalPages, setTotalPages] = useState(1);
  const [filterText, setFilterText] = useState("");
  const [deletingId, setDeletingId] = useState(null);

  const fetchStories = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `/api/stories/?page=${page}&page_size=${pageSize}`,
      );
      setStories(res.data.results);
      setTotalPages(Math.ceil(res.data.count / pageSize) || 1);
    } catch (err) {
      toast.error("Failed to load stories");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchStories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this story?")) return;
    setDeletingId(id);
    try {
      await axios.delete(`/api/stories/${id}/delete/`);
      toast.success("Story deleted!");
      fetchStories();
    } catch (err) {
      toast.error("Delete failed");
    } finally {
      setDeletingId(null);
    }
  };

  const filteredStories = stories.filter(
    (story) =>
      story.headline?.toLowerCase().includes(filterText.toLowerCase()) ||
      story.description?.toLowerCase().includes(filterText.toLowerCase()),
  );

  const isVideo = (url) =>
    url && (url.endsWith(".mp4") || url.endsWith(".mov"));

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatTime = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getPageNumbers = () => {
    const pages = [];
    const delta = 1;
    const range = [];
    for (
      let i = Math.max(2, page - delta);
      i <= Math.min(totalPages - 1, page + delta);
      i++
    ) {
      range.push(i);
    }
    if (page - delta > 2) range.unshift("ellipsis-start");
    if (page + delta < totalPages - 1) range.push("ellipsis-end");
    pages.push(1);
    pages.push(...range);
    if (totalPages > 1) pages.push(totalPages);
    return [...new Set(pages)];
  };

  return (
    <div className="min-h-screen bg-slate-50 px-3 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-6xl">
        {/* Header */}
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-600 text-white shadow-sm">
              <FaNewspaper size={16} />
            </span>
            <div>
              <h1 className="text-xl font-semibold tracking-tight text-slate-900 sm:text-2xl">
                Blog Stories
              </h1>
              <p className="text-sm text-slate-500">
                {stories.length > 0
                  ? `${totalPages} page${totalPages > 1 ? "s" : ""} of stories`
                  : "Manage published stories"}
              </p>
            </div>
          </div>

          <Link
            to="/admin-html-secure-metro/blog"
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 active:bg-emerald-800"
          >
            <FaPlus size={12} />
            Add Blog
          </Link>
        </div>

        {/* Toolbar */}
        <div className="mb-4 flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
          <div className="relative w-full sm:max-w-sm">
            <FaSearch
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              size={14}
            />
            <input
              type="text"
              placeholder="Search by headline or description..."
              className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2.5 pl-9 pr-4 text-sm text-slate-800 placeholder:text-slate-400 transition focus:border-emerald-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-100"
              value={filterText}
              onChange={(e) => {
                setPage(1);
                setFilterText(e.target.value);
              }}
            />
          </div>

          <div className="flex items-center justify-between gap-2 sm:justify-end">
            <label className="whitespace-nowrap text-sm font-medium text-slate-600">
              Rows per page
            </label>
            <select
              value={pageSize}
              onChange={(e) => {
                setPage(1);
                setPageSize(Number(e.target.value));
                localStorage.setItem("pageSize", e.target.value);
              }}
              className="cursor-pointer rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-medium text-slate-700 transition focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>

        {/* ===== Desktop / tablet table ===== */}
        <div className="hidden overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm md:block">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50/80 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <th className="px-5 py-3.5 w-12">#</th>
                  <th className="px-5 py-3.5">Headline</th>
                  <th className="px-5 py-3.5">Description</th>
                  <th className="px-5 py-3.5">Media</th>
                  <th className="px-5 py-3.5">Published</th>
                  <th className="px-5 py-3.5 text-center">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-5 py-12 text-center text-slate-400"
                    >
                      <span className="inline-flex items-center gap-2 text-sm">
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-emerald-500" />
                        Loading stories...
                      </span>
                    </td>
                  </tr>
                ) : filteredStories.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-5 py-14 text-center">
                      <div className="flex flex-col items-center gap-2 text-slate-400">
                        <FaNewspaper size={28} />
                        <p className="text-sm font-medium text-slate-500">
                          No stories found
                        </p>
                        <p className="text-xs">
                          Try a different search, or add your first story
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredStories.map((story, index) => (
                    <tr
                      key={story.id}
                      className="group transition-colors hover:bg-emerald-50/40"
                    >
                      <td className="px-5 py-4 align-top text-slate-400">
                        {(page - 1) * pageSize + index + 1}
                      </td>
                      <td className="px-5 py-4 align-top font-medium text-slate-800">
                        {story.headline || "-"}
                      </td>
                      <td className="px-5 py-4 align-top">
                        <p className="max-w-xs text-slate-600 line-clamp-2">
                          {story.description || "-"}
                        </p>
                      </td>
                      <td className="px-5 py-4 align-top">
                        {story.media ? (
                          isVideo(story.media) ? (
                            <span className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-50 px-2.5 py-1.5 text-xs font-medium text-emerald-700">
                              <FaPlayCircle size={12} />
                              Video
                            </span>
                          ) : (
                            <img
                              src={story.media}
                              alt={story.headline || "story media"}
                              className="h-14 w-14 rounded-lg border border-slate-200 object-cover"
                            />
                          )
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      <td className="px-5 py-4 align-top text-slate-500">
                        <div className="flex flex-col">
                          <span className="font-medium text-slate-700">
                            {formatDate(story.created_at)}
                          </span>
                          <span className="text-xs">
                            {formatTime(story.created_at)}
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-4 text-center align-top">
                        <button
                          onClick={() => handleDelete(story.id)}
                          disabled={deletingId === story.id}
                          aria-label={`Delete story ${story.headline}`}
                          className="rounded-full p-2.5 text-slate-400 transition hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                        >
                          {deletingId === story.id ? (
                            <span className="block h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-red-500" />
                          ) : (
                            <FaTrash size={14} />
                          )}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* ===== Mobile cards ===== */}
        <div className="space-y-3 md:hidden">
          {loading ? (
            <div className="flex items-center justify-center rounded-2xl border border-slate-200 bg-white py-12 text-slate-400 shadow-sm">
              <span className="inline-flex items-center gap-2 text-sm">
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-emerald-500" />
                Loading stories...
              </span>
            </div>
          ) : filteredStories.length === 0 ? (
            <div className="flex flex-col items-center gap-2 rounded-2xl border border-slate-200 bg-white py-14 text-slate-400 shadow-sm">
              <FaNewspaper size={28} />
              <p className="text-sm font-medium text-slate-500">
                No stories found
              </p>
              <p className="text-xs">
                Try a different search, or add your first story
              </p>
            </div>
          ) : (
            filteredStories.map((story, index) => (
              <div
                key={story.id}
                className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    {story.media ? (
                      isVideo(story.media) ? (
                        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
                          <FaPlayCircle size={18} />
                        </span>
                      ) : (
                        <img
                          src={story.media}
                          alt={story.headline || "story media"}
                          className="h-12 w-12 shrink-0 rounded-lg border border-slate-200 object-cover"
                        />
                      )
                    ) : (
                      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-400">
                        <FaNewspaper size={16} />
                      </span>
                    )}
                    <div>
                      <p className="font-medium text-slate-800">
                        {story.headline || "-"}
                      </p>
                      <p className="text-xs text-slate-400">
                        {formatDate(story.created_at)} &middot;{" "}
                        {formatTime(story.created_at)}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDelete(story.id)}
                    disabled={deletingId === story.id}
                    aria-label={`Delete story ${story.headline}`}
                    className="shrink-0 rounded-full p-2.5 text-slate-400 transition hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                  >
                    {deletingId === story.id ? (
                      <span className="block h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-red-500" />
                    ) : (
                      <FaTrash size={14} />
                    )}
                  </button>
                </div>

                {story.description && (
                  <p className="mt-3 rounded-xl bg-slate-50 p-3 text-sm leading-relaxed text-slate-600">
                    {story.description}
                  </p>
                )}
              </div>
            ))
          )}
        </div>

        {/* Pagination */}
        {!loading && filteredStories.length > 0 && (
          <div className="mt-5 flex flex-col items-center justify-between gap-3 sm:flex-row">
            <p className="text-sm text-slate-500">
              Page <span className="font-medium text-slate-700">{page}</span> of{" "}
              <span className="font-medium text-slate-700">{totalPages}</span>
            </p>

            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Prev
              </button>

              <div className="flex items-center gap-1">
                {getPageNumbers().map((p, i) =>
                  typeof p === "string" ? (
                    <span
                      key={p + i}
                      className="px-1.5 text-sm text-slate-400 select-none"
                    >
                      &hellip;
                    </span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      className={`min-w-[2.25rem] rounded-lg px-2.5 py-1.5 text-sm font-medium transition ${
                        page === p
                          ? "bg-emerald-600 text-white shadow-sm"
                          : "text-slate-600 hover:bg-slate-100"
                      }`}
                    >
                      {p}
                    </button>
                  ),
                )}
              </div>

              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogTable;
